/**
 * Task 26: Manager Dashboard with Recruiter Creation
 * Managers can create recruiters (pending admin approval)
 */

import { useState, useEffect } from 'react';
import { UserPlus, Clock, CheckCircle, AlertCircle, PhoneIncoming } from 'lucide-react';
import axiosInstance from '../config/axiosConfig';
import CallLogsTab from './CallLogsTab';

const ManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [currentUser, setCurrentUser] = useState(null);
  const [myRecruiters, setMyRecruiters] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    mobile: '',
    location: ''
  });

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchMyRecruiters();
    }
  }, [currentUser]);

  const fetchCurrentUser = async () => {
    try {
      console.log('[ManagerDashboard] Fetching current user from /auth/me...');
      const token = localStorage.getItem('spes_token');
      console.log('[ManagerDashboard] Token exists:', !!token);
      
      const response = await axiosInstance.get('/auth/me');
      console.log('[ManagerDashboard] Successfully fetched user:', response.data);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('[ManagerDashboard] ❌ Error fetching user:', error);
      console.error('[ManagerDashboard] Error response:', error.response);
      console.error('[ManagerDashboard] Error status:', error.response?.status);
      // DO NOT navigate here - let axios interceptor handle it
    }
  };

  const fetchMyRecruiters = async () => {
    try {
      console.log('[ManagerDashboard] Fetching recruiters from /users/...');
      const response = await axiosInstance.get('/users/');
      console.log('[ManagerDashboard] Successfully fetched users:', response.data);
      
      // Task 27H: Backend now filters by manager_id, so all returned users are this manager's recruiters
      const recruiters = response.data.filter(u => u.role === 'recruiter');
      console.log('[ManagerDashboard] Filtered recruiters:', recruiters.length);
      setMyRecruiters(recruiters);
    } catch (error) {
      console.error('[ManagerDashboard] ❌ Error fetching recruiters:', error);
      console.error('[ManagerDashboard] Error status:', error.response?.status);
      
      if (error.response?.status === 403) {
        console.error('[ManagerDashboard] 403 Forbidden - Manager does not have permission');
        setToast({ message: 'Access denied. Contact your administrator.', type: 'error' });
      }
      // Don't navigate - axios interceptor handles 401 logout
    }
  };

  const handleCreateRecruiter = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!currentUser) {
      setToast({ message: 'User session not loaded. Please refresh.', type: 'error' });
      setLoading(false);
      return;
    }

    try {
      await axiosInstance.post('/users/create', {
        ...createForm,
        role: 'recruiter',
        manager_id: currentUser.id
      });
      
      setToast({ 
        message: 'Recruiter created! Pending admin approval.', 
        type: 'success' 
      });
      
      setCreateForm({ name: '', email: '', mobile: '', location: '' });
      fetchMyRecruiters();
    } catch (error) {
      setToast({ 
        message: error.response?.data?.detail || 'Failed to create recruiter', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'create', label: 'Create Recruiter', icon: UserPlus },
    { id: 'status', label: 'My Recruiters', icon: Clock },
    { id: 'calls', label: 'Call Logs', icon: PhoneIncoming }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background p-6">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-6">
        <h1 className="text-3xl font-bold text-textLight font-heading mb-2">
          Manager Dashboard
        </h1>
        <p className="text-textLight/60">Create and manage your team of recruiters</p>
      </div>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto mb-6">
        <div className="card-light rounded-lg shadow-md p-2 flex gap-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-teal to-gold text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto">
        {/* Create Recruiter Tab */}
        {activeTab === 'create' && (
          <div className="card-light rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6">Create New Recruiter</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Approval Required</p>
                <p>Recruiters you create will be pending until approved by an administrator. 
                   They will receive login credentials once approved.</p>
              </div>
            </div>

            <form onSubmit={handleCreateRecruiter} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                  placeholder="e.g., John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                  placeholder="john.doe@spes.in"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={createForm.mobile}
                    onChange={(e) => setCreateForm({ ...createForm, mobile: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                    placeholder="9876543210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={createForm.location}
                    onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                    placeholder="e.g., Pune"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-teal to-gold text-white rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Recruiter'}
              </button>
            </form>
          </div>
        )}

        {/* My Recruiters Status Tab */}
        {activeTab === 'status' && (
          <div className="card-light rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-6">My Recruiters</h2>
            
            {myRecruiters.length === 0 ? (
              <div className="text-center py-12">
                <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="mb-4">No recruiters created yet</p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="px-6 py-2 bg-gradient-to-r from-teal to-gold text-white rounded-lg font-semibold hover:shadow-lg transition"
                >
                  Create First Recruiter
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {myRecruiters.map(recruiter => (
                  <div 
                    key={recruiter.id} 
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {recruiter.name}
                        </h3>
                        <p className="text-sm mt-1">
                          {recruiter.email}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-sm">
                          {recruiter.mobile && (
                            <span>📱 {recruiter.mobile}</span>
                          )}
                          {recruiter.location && (
                            <span>📍 {recruiter.location}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        {recruiter.approved ? (
                          <span className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg font-semibold text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg font-semibold text-sm">
                            <Clock className="w-4 h-4" />
                            Pending Approval
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {recruiter.must_change_password && recruiter.approved && (
                      <div className="mt-3 text-sm text-orange-600 bg-orange-50 rounded px-3 py-2">
                        ⚠️ First login pending - awaiting password reset
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {myRecruiters.filter(r => r.approved).length}
                </div>
                <div className="text-sm text-green-700">Active Recruiters</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {myRecruiters.filter(r => !r.approved).length}
                </div>
                <div className="text-sm text-yellow-700">Pending Approval</div>
              </div>
            </div>
          </div>
        )}

        {/* Task 29: Call Logs Tab */}
        {activeTab === 'calls' && (
          <div className="max-w-5xl mx-auto">
            <CallLogsTab currentUser={currentUser} />
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div 
          className={`fixed top-4 right-4 z-50 ${
            toast.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
          } border rounded-lg p-4 shadow-lg animate-slide-in`}
        >
          <div className="flex items-center gap-2">
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <p className={toast.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {toast.message}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;
